/**
 * Termos da assinatura no checkout — régua ÚNICA do texto da oferta (cards de
 * plano), do rodapé fixo do celular, do resumo e do quadro acima do botão de
 * pagar. Espelho no app: app/src/utils/termosPlano.ts (mudou aqui, mudar lá).
 *
 * Google Play recusou o build 40 (28/10/2026, "Terms of subscription offer are
 * unclear"): a oferta precisa dizer quanto custa, de quanto em quanto tempo
 * cobra, se renova sozinha e o que acontece ao sair antes — na oferta E no
 * carrinho, com o valor COBRADO em destaque (no anual, o ano; a parcela vem
 * abaixo). Vale SÓ dentro do app (WebView, `isInApp`): na web, com o ano em
 * destaque, quem abria o checkout e preenchia o passo 1 caiu de ~55% pra ~30%
 * (28–29/10/2026), e a web voltou ao valor do mês no anual, sem o quadro.
 *
 * Fatos de cobrança conferidos no backend em 28/10/2026 (mudou lá, mudar aqui):
 * - Anual é `billing_cycle = 'annual'`: é o que o pagamento lê
 *   (payments.service, `isAnual = plan.billingCycle === 'annual'`). Os outros
 *   cobram por mês; `monthly_annual` e `annual_installment` com contrato de 12.
 * - Anual no cartão: o ano inteiro numa transação em 12x (payments.service,
 *   `installments: isAnual ? 12 : 1`). No vencimento o cron cobra o ano de novo,
 *   também em 12x (recurring-payments.cron + `cardInstallmentsFor`).
 * - Anual no PIX (à vista): ninguém cobra sozinho — o cron de cartão pula PIX e
 *   o lembrete das 9h exclui `annual`; sem renovar, encerra após o vencimento.
 * - Mensal no PIX: a cada vencimento o cron das 9h gera um PIX novo e manda pelo
 *   WhatsApp (pix-recurring-whatsapp.cron).
 * - Contrato de 12 meses pago por mês: na 12ª parcela o cron renova por mais 12.
 * - Cupom em plano cobrado por mês:
 *   · cartão: vale só na 1ª cobrança — o cron cobra o preço cheio do plano
 *     (`resolveRecurringCharge`, recurring-charge.ts);
 *   · PIX: vale TODO mês — o PIX do mês sai de `subscription.paidAmount`
 *     (`generatePixLink`, subscriptions.service), gravado com o cupom no
 *     webhook (payments.service, `paidAmount = originalAmount - desconto`).
 * - Multa: 30% do restante, só nos 12 primeiros meses do contrato (cláusula
 *   2.2), e nenhuma até 7 dias após a entrega do kit 1
 *   (subscriptions/utils/fidelidade.ts, `calcularMultaCancelamento`).
 * - Renovação "retomar" (/renovar, mesma assinatura mensal): o pagamento
 *   reativa a assinatura sem regravar `paidAmount` (`applyRenewalTakeover`,
 *   payments.service) — o PIX dos meses seguintes sai do `paidAmount` ANTIGO.
 *   Então o PIX do retomar não promete valor futuro. "refazer" cria assinatura
 *   nova com `paidAmount` = preço com cupom.
 * - O checkout PÚBLICO também pode virar "retomar": o PIX de ex-assinante
 *   (mesmo cliente + CPF, uma sub elegível, mesmo plano mensal) ganha o
 *   vínculo implícito (`resolveImplicitRenewalStamp` → `decideImplicitRenewal`
 *   → `resolveEffectiveRenewalMode`, flag IMPLICIT_RENEWAL_MODE) e passa pelo
 *   mesmo `applyRenewalTakeover`. A página não sabe se haverá vínculo: o PIX
 *   mensal do checkout diz o valor da assinatura nova e avisa que, na
 *   retomada, vale o valor da anterior. Cartão não é afetado (o cron cobra o
 *   preço do plano).
 */

export type FormaDePagamento = 'credit-card' | 'pix';

/** O que o assinante contrata: mensal puro, mensal com contrato de 12 meses ou anual. */
export type ContratoDoPlano = 'mensal' | 'mensal_12_meses' | 'anual';

export interface PlanoDosTermos {
  billingCycle?: string | null;
  planType?: string | null;
}

export interface EntradaTermos {
  plano: PlanoDosTermos | null | undefined;
  forma: FormaDePagamento;
  /** Preço de tabela, sem cupom: o ANO inteiro no anual, o mês nos outros. */
  precoTabela: number;
  /** O mesmo preço com o cupom que vale nesta forma de pagamento. */
  precoComCupom: number;
  /**
   * Renovação (/renovar): a multa sai condicional. Ela só vale no 1º ciclo
   * (`cicloCorrente` = 0) e a renovação "refazer" herda a sequência de kits
   * da assinatura anterior — com 12 kits ou mais a assinatura nova já nasce
   * fora do 1º ciclo. A página não tem o nº do kit; quem calcula é o atendente
   * (`calcularMultaCancelamento`).
   */
  renovacao?: boolean;
  /**
   * Modo EFETIVO da renovação (espelho de `resolveEffectiveRenewalMode`). No
   * PIX mensal, só "refazer" permite dizer o valor dos próximos meses; sem o
   * modo (ou "retomar"), o texto não promete valor futuro.
   */
  modoRenovacao?: 'retomar' | 'refazer';
}

/** Barra fixa do celular: o valor cobrado em destaque e a linha de frequência. */
export interface RodapeDoPreco {
  /** O valor em destaque: o ano no anual no cartão, o mês nos mensais. */
  valor: string;
  /** Depois do valor, menor ("/ano", "/mês"). */
  sufixo: string | null;
  /** Parcela/frequência e renovação, numa linha curta. */
  detalhe: string;
}

export interface TermosAssinatura {
  contrato: ContratoDoPlano;
  rodape: RodapeDoPreco;
  /** O rodapé numa linha só (resumo do desktop). */
  resumo: string;
  /** Termos completos, uma frase por item: preço e frequência, renovação, fidelidade, cancelamento. */
  linhas: string[];
}

export const CANCELAMENTO =
  'Para cancelar, solicite pelo app (Minha assinatura) ou fale com a nossa equipe.';
export const SEM_FIDELIDADE = 'Sem fidelidade: cancele quando quiser, sem multa.';
export const FIDELIDADE =
  'Fidelidade só nos 12 primeiros meses: cancelar antes disso gera multa de 30% do valor restante (sem multa até 7 dias após a entrega do 1º kit).';
/**
 * Renovação: verdadeira nos dois casos (menos de 12 kits = 1º ciclo, multa;
 * 12 ou mais = fora dele, sem multa) e nos prazos sem multa — por isso "pode".
 */
export const FIDELIDADE_RENOVACAO =
  'Se a sua assinatura ainda não completou 12 kits, cancelar antes disso pode gerar multa de 30% do valor restante.';
/** PIX do "retomar": os próximos PIX saem do valor gravado na assinatura. */
export const PIX_PROXIMOS_MESES =
  'Nos próximos meses enviamos um novo PIX pelo WhatsApp com o valor da sua assinatura';
/** PIX mensal do checkout público: o valor da assinatura nova, e a ressalva da retomada. */
export const PIX_PROXIMOS_MESES_PUBLICO = (valor: string) =>
  `Nos próximos meses, um novo PIX de ${valor} chega pelo WhatsApp; se você está retomando uma assinatura anterior, vale o valor dela.`;
/** Versão curta dos cards de plano — mesma frase do app (MULTA_12_MESES). */
export const MULTA_12_MESES =
  'Cancelar nos 12 primeiros meses gera multa de 30% do valor restante (sem multa até 7 dias após a entrega do 1º kit).';

/** "R$ 1.078,80" — com separador de milhar, que o total do ano pode passar. */
export function formatarReais(valor: number): string {
  const n = Number.isFinite(valor) ? Math.max(0, valor) : 0;
  const [inteiro, centavos] = n.toFixed(2).split('.');
  return `R$ ${inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${centavos}`;
}

/**
 * Anual é o CICLO anual — o mesmo critério do pagamento (12x do ano) e do
 * `isAnual` do checkout. `plan_type = 'annual'` num ciclo mensal (combinação
 * que o painel já recusa, `planCycleMismatchError`) cobra o mês: fica como
 * contrato de 12 meses pago por mês, sem prometer "12x por ano".
 */
export function contratoDoPlano(plano: PlanoDosTermos | null | undefined): ContratoDoPlano {
  if (plano?.billingCycle === 'annual') return 'anual';
  const tipo = plano?.planType;
  if (tipo === 'monthly_annual' || tipo === 'annual_installment' || tipo === 'annual') {
    return 'mensal_12_meses';
  }
  return 'mensal';
}

export function termosDaAssinatura(entrada: EntradaTermos): TermosAssinatura {
  const contrato = contratoDoPlano(entrada.plano);
  const tabela = Number(entrada.precoTabela) || 0;
  const comCupom = Number(entrada.precoComCupom) || 0;
  const fidelidade = entrada.renovacao ? [FIDELIDADE_RENOVACAO] : [FIDELIDADE];

  if (contrato === 'anual') {
    const total = formatarReais(comCupom);
    // Anual no PIX só existe à vista (checkout com `pixAVista`, renovação
    // sempre): `pixNoCheckout`/`pixNaRenovacao`. A parcela no PIX não é oferecida.
    if (entrada.forma === 'pix') {
      return {
        contrato,
        // Mesma copy de `precoNoPix().detalhe` — a barra não pode se contradizer.
        rodape: { valor: total, sufixo: null, detalhe: 'à vista no PIX · vale por 12 meses' },
        resumo: `${total} à vista no PIX · vale por 12 meses`,
        linhas: [
          `${total} à vista no PIX: um pagamento só, válido por 12 meses.`,
          'Não renova sozinho: para continuar depois dos 12 meses, você renova com um novo pagamento.',
          ...fidelidade,
          CANCELAMENTO,
        ],
      };
    }
    const parcela = formatarReais(comCupom / 12);
    return {
      contrato,
      rodape: {
        valor: total,
        sufixo: '/ano',
        detalhe: `em 12x de ${parcela} no cartão · renova automaticamente`,
      },
      resumo: `${total} por ano, em 12x de ${parcela} no cartão · renova automaticamente`,
      linhas: [
        `${total} por ano, parcelados em 12x de ${parcela} no cartão.`,
        'Renova automaticamente a cada 12 meses, com nova cobrança do ano no cartão.',
        ...fidelidade,
        CANCELAMENTO,
      ],
    };
  }

  const pix = entrada.forma === 'pix';
  // PIX na renovação sem ser "refazer": o próximo PIX sai do paidAmount antigo
  // da assinatura (o retomar não o regrava) — não dá pra dizer o valor.
  const semValorFuturo = pix && !!entrada.renovacao && entrada.modoRenovacao !== 'refazer';
  // Cupom no cartão desconta só a 1ª cobrança (o cron cobra o cheio); no PIX
  // ele fica em todo PIX do mês (sai do paidAmount) — então no PIX o preço do
  // mês JÁ É o preço com cupom.
  const soNoPrimeiroMes = !pix && tabela - comCupom > 0.005;
  const mes = formatarReais(pix ? comCupom : tabela);
  const primeiro = formatarReais(comCupom);
  const meio = pix ? 'no PIX' : 'no cartão';
  const precoDaLinha = soNoPrimeiroMes
    ? `${primeiro} no 1º mês com o cupom; depois, ${mes} por mês`
    : `${mes} por mês`;
  const resumoDoPreco = soNoPrimeiroMes ? `${primeiro} no 1º mês, depois ${mes}` : mes;
  const rodapeComCupom = (depois: string): RodapeDoPreco => ({
    valor: primeiro,
    sufixo: null,
    detalhe: `no 1º mês · depois ${mes}/mês${depois}`,
  });

  if (semValorFuturo) {
    const comContrato = contrato === 'mensal_12_meses';
    return {
      contrato,
      rodape: {
        valor: primeiro,
        sufixo: null,
        detalhe: comContrato ? 'PIX deste mês · contrato de 12 meses' : 'PIX deste mês · depois um PIX por mês',
      },
      resumo: `${primeiro} no PIX deste mês · ${comContrato ? 'contrato de 12 meses' : 'renova todo mês'}`,
      linhas: [
        comContrato
          ? `Este PIX: ${primeiro}, referente a este mês, em contrato de 12 meses.`
          : `Este PIX: ${primeiro}, referente a este mês.`,
        comContrato
          ? `${PIX_PROXIMOS_MESES}; ao fim dos 12 meses o contrato renova sozinho por mais 12.`
          : `${PIX_PROXIMOS_MESES}; ela continua até você cancelar.`,
        ...(comContrato ? fidelidade : [SEM_FIDELIDADE]),
        CANCELAMENTO,
      ],
    };
  }

  // PIX mensal no checkout público: verdadeiro com e sem vínculo implícito.
  if (pix && !entrada.renovacao) {
    const comContrato = contrato === 'mensal_12_meses';
    return {
      contrato,
      rodape: {
        valor: primeiro,
        sufixo: null,
        detalhe: comContrato
          ? 'neste PIX · contrato de 12 meses'
          : 'neste PIX · depois um PIX por mês pelo WhatsApp',
      },
      resumo: `${primeiro} neste PIX · ${comContrato ? 'contrato de 12 meses' : 'depois um PIX por mês'}`,
      linhas: [
        comContrato
          ? `Este PIX: ${primeiro}, referente ao 1º mês do contrato de 12 meses.`
          : `Este PIX: ${primeiro}, referente ao 1º mês.`,
        `${PIX_PROXIMOS_MESES_PUBLICO(primeiro)}${
          comContrato ? ' Ao fim dos 12 meses o contrato renova sozinho por mais 12.' : ' A assinatura continua até você cancelar.'
        }`,
        ...(comContrato ? fidelidade : [SEM_FIDELIDADE]),
        CANCELAMENTO,
      ],
    };
  }

  if (contrato === 'mensal_12_meses') {
    return {
      contrato,
      rodape: soNoPrimeiroMes
        ? rodapeComCupom(' · contrato de 12 meses')
        : {
            valor: mes,
            sufixo: '/mês',
            detalhe: pix
              ? 'Contrato de 12 meses · um PIX por mês'
              : 'Contrato de 12 meses · renova automaticamente',
          },
      resumo: `${resumoDoPreco} por mês ${meio} · contrato de 12 meses`,
      linhas: [
        pix
          ? `${precoDaLinha}, pagos por PIX, em contrato de 12 meses.`
          : `${precoDaLinha}, cobrados automaticamente no cartão, em contrato de 12 meses.`,
        pix
          ? 'Todo mês enviamos um novo PIX pelo WhatsApp; ao fim dos 12 meses o contrato renova sozinho por mais 12.'
          : 'Ao fim dos 12 meses, o contrato renova sozinho por mais 12 e as mensalidades continuam.',
        ...fidelidade,
        CANCELAMENTO,
      ],
    };
  }

  return {
    contrato,
    rodape: soNoPrimeiroMes
      ? rodapeComCupom('')
      : {
          valor: mes,
          sufixo: '/mês',
          detalhe: pix ? 'Um PIX por mês · cancele quando quiser' : 'Renova todo mês · cancele quando quiser',
        },
    resumo: `${resumoDoPreco} por mês ${meio} · renova todo mês`,
    linhas: [
      pix ? `${precoDaLinha}, pagos por PIX.` : `${precoDaLinha}, cobrados automaticamente no cartão.`,
      pix
        ? 'Todo mês enviamos um novo PIX pelo WhatsApp; a assinatura continua até você cancelar.'
        : 'A assinatura renova sozinha a cada mês, até você cancelar.',
      SEM_FIDELIDADE,
      CANCELAMENTO,
    ],
  };
}

/** Oferta de um card de plano (troca de plano, renovação): mesma régua do app. */
export interface OfertaDoPlano {
  contrato: ContratoDoPlano;
  /** Valor em destaque: o que é cobrado — o ANO no anual, o mês nos outros. */
  preco: string;
  /** Logo depois do preço: "/ano" ou "/mês". */
  sufixo: string;
  /** Linha sob o preço, só no anual: a parcela no cartão. */
  parcela: string | null;
  /** Preço "De ... por", na mesma unidade do destaque; null sem desconto. */
  precoOriginal: string | null;
  /** Termos curtos do card: frequência + renovação, e fidelidade. */
  termos: string[];
}

export function ofertaDoPlano(
  plano: PlanoDosTermos & { precoTabela: number; precoOriginal?: number | null },
  /**
   * Web (fora do app): o card como era antes de 28/10/2026 — anual pelo valor
   * do mês, com "12x de … = total" abaixo, e uma linha só de fidelidade.
   */
  opcoes: { anualPorMes?: boolean } = {},
): OfertaDoPlano {
  const contrato = contratoDoPlano(plano);
  const preco = Number(plano.precoTabela) || 0;
  const original = Number(plano.precoOriginal);
  const precoOriginal = Number.isFinite(original) && original > preco ? formatarReais(original) : null;
  if (opcoes.anualPorMes) {
    const anual = contrato === 'anual';
    return {
      contrato,
      preco: formatarReais(anual ? preco / 12 : preco),
      sufixo: '/mês',
      parcela: anual ? `12x de ${formatarReais(preco / 12)} = ${formatarReais(preco)}` : null,
      precoOriginal: precoOriginal && (anual ? formatarReais(original / 12) : precoOriginal),
      // Contrato de 12 meses pago por mês também tem fidelidade: "cancele
      // quando quiser" nele seria promessa falsa.
      termos: [contrato === 'mensal' ? 'Cancele quando quiser' : 'Fidelidade de 12 meses'],
    };
  }
  if (contrato === 'anual') {
    return {
      contrato,
      preco: formatarReais(preco),
      sufixo: '/ano',
      parcela: `em 12x de ${formatarReais(preco / 12)} no cartão`,
      precoOriginal,
      termos: ['Cobrança anual no cartão, renovada automaticamente a cada 12 meses.', MULTA_12_MESES],
    };
  }
  if (contrato === 'mensal_12_meses') {
    return {
      contrato,
      preco: formatarReais(preco),
      sufixo: '/mês',
      parcela: null,
      precoOriginal,
      termos: ['Cobrança mensal, com contrato de 12 meses renovado automaticamente.', MULTA_12_MESES],
    };
  }
  return {
    contrato,
    preco: formatarReais(preco),
    sufixo: '/mês',
    parcela: null,
    precoOriginal,
    termos: ['Cobrança mensal, renovada automaticamente todo mês.', 'Sem fidelidade: cancele quando quiser.'],
  };
}

/**
 * Diferença de preço até o plano de cima (modal "Mude para o Explorador"), na
 * mesma unidade de cobrança: no anual o ano (e a parcela), no mensal o mês.
 * null quando não dá pra afirmar (sem o plano-alvo, ciclos diferentes,
 * diferença não positiva) — melhor não dizer valor do que dizer valor errado.
 */
export function diferencaDeUpgrade(
  atual: PlanoDosTermos & { precoTabela: number },
  alvo: (PlanoDosTermos & { precoTabela: number }) | null | undefined,
  /** Web: o anual fala pelo mês ("R$ 10,00 por mês"), como o resto da página. */
  opcoes: { anualPorMes?: boolean } = {},
): string | null {
  if (!alvo) return null;
  const anual = contratoDoPlano(atual) === 'anual';
  if (anual !== (contratoDoPlano(alvo) === 'anual')) return null;
  const diferenca = (Number(alvo.precoTabela) || 0) - (Number(atual.precoTabela) || 0);
  if (!(diferenca > 0.005)) return null;
  if (anual && opcoes.anualPorMes) return `${formatarReais(diferenca / 12)} por mês`;
  return anual
    ? `${formatarReais(diferenca)} por ano (12x de ${formatarReais(diferenca / 12)} no cartão)`
    : `${formatarReais(diferenca)} por mês`;
}
